//Utilities
import AppDispatcher from '../core/AppDispatcher.jsx';
import { EventEmitter } from 'events';
import _ from 'lodash';
import ApplicationConstants from '../constants/ApplicationConstants.js';
import IDVerificationConstants from '../constants/IDVerificationConstants.js';
import DigitalConsentConstants from '../constants/DigitalConsentConstants.js';
import DownloadConstants from '../constants/DownloadConstants';
import DeclarationConstants from '../constants/DeclarationConstants';
import Products from '../utils/Products.js';
import AccountTypes from '../utils/AccountTypes.js';
import DigitalConsentEligibilityHelper from '../utils/DigitalConsentEligibilityHelper.js';
import DownloadFormsHelper from '../utils/DownloadFormsHelper.js';
import WorkItemHelper from '../utils/WorkItemHelper.js';
import AuditWorkItemHelper from '../utils/AuditWorkItemHelper.js';
import IdVerificationHelper from '../utils/IdVerificationHelper.js';
import DateTime from '../utils/DateTime.js';

//local variables
const CHANGE_EVENT = 'change';
const ApplicationActionTypes = ApplicationConstants.ActionTypes;
const ActionTypes = IDVerificationConstants.ActionTypes;
const DCActionTypes = DigitalConsentConstants.ActionTypes;
const Eligibility = IDVerificationConstants.Eligibility;

let _formModel = {},
    _consolidatedFormModel = {},
    _product = {},
    _dcEligibility = {
        eligible: Eligibility.NotEligible,
        checkComplete: false
    },
    _applicationSubmitting = false,
    _waitingForDigitalConsentKillSwitch = false,
    _digitalConsentCheckEnabled = false,
    _showMoreMenu = false,
    _checkPerformed = false,
    _accountNumber = '',
    _hasSubmitFailed,
    _clientIdentificationMode,
    _accountType = '',
    _showDownloadMenu = false,
    _formsToDownload = [],
    _downloadInProgress = false,
    _selectedForms = [],
    _readyForDownload = false,
    _downloadedForms = '',
    _formDownloaded = true,
    _downloadFormsFailed = false,
    _mobileNumberVerificationResult,
    _hideSpinner = false,
    _clientIp = '';

function setDigitalConsentKillSwitch(killSwitch) {
    _digitalConsentCheckEnabled = killSwitch && killSwitch.value === 'OFF';
    if (!_digitalConsentCheckEnabled) {
        _dcEligibility = _.cloneDeep({
            eligible: Eligibility.NotRequired,
            checkComplete: true
        });
    }
}

function resetStore(hasSubmitFailed) {
    if (hasSubmitFailed) {
        _dcEligibility = {
            eligible: Eligibility.NotEligible,
            checkComplete: false
        };
    }
    _applicationSubmitting = false;
    _hasSubmitFailed = hasSubmitFailed;
    _accountNumber = '';
    _digitalConsentCheckEnabled = false;
}

function toggleEligibilityCheckComplete() {
    const isEligible = _dcEligibility.eligible;
    const isCheckComplete = _dcEligibility.checkComplete;
    _dcEligibility = {
        eligible: isEligible,
        checkComplete: !isCheckComplete
    }
}

function performDigitalConsentEligibilityCheck(clientIp) {
    if(DigitalConsentEligibilityHelper.checkProductEligibility(_product.value)){
        if (Products.isInvestment(_product.value)) {
            if (DigitalConsentEligibilityHelper.checkAccountTypeEligibility(_formModel.form.accountType)) {
                if (AccountTypes.checkIfJointAccount(_formModel.form.accountType)) {
                    performJointCheck();
                } else {
                    performIndividualCheck();
                }
            }
        } else {
            performIndividualCheck();
        }
    }

    if (!_checkPerformed) {
        _dcEligibility = {
            eligible: Eligibility.NotEligible,
            checkComplete:true
        }
    }

    if (clientIp) {
        if (_formModel) {
            _formModel.clientIp = clientIp;
        }
        if (_consolidatedFormModel) {
            _consolidatedFormModel.clientIp = clientIp;
        }
    }
}

function performIndividualCheck() {
    _checkPerformed = true;
    let applicationUpdatedWithKyc = IdVerificationHelper.verifyKycUpdate(_formModel);
    let individualRequest = {
        oin: applicationUpdatedWithKyc.form.yourDetails.oin,
        mobileNumber: applicationUpdatedWithKyc.form.yourDetails.contactDetails.mobile,
        kycFlag: applicationUpdatedWithKyc.form.yourDetails.kycFlag,
        userType: window.NOA.userContext.userType,
        verificationResult: _mobileNumberVerificationResult,
        productId: _product.value
    };
    _dcEligibility = DigitalConsentEligibilityHelper.individualEligibilityCheck(individualRequest);

    //if the user is an investor and it is a super application, DIGITAL CONSENT is SET TO YES WHEN THEY CLICK AGREE
    //OR if the user is an investor and he/she is not required to do digital consent then DIGITAL CONSENT is SET TO YES
    if ((Products.isSuper(_product.value) && window.NOA.userContext.isInvestor) || (_dcEligibility.eligible === Eligibility.NotRequired && window.NOA.userContext.isExisting)) {
        _formModel.hasDigitalSignature = ApplicationConstants.YesNoValues.YES;
        _formModel.DigitalSignatureTimeStamp = DateTime.getCurrentDate();

        const isFni = window.NOA.userContext.isInvestor && window.NOA.userContext.isExisting;
        const workItem = AuditWorkItemHelper.generateApplicationConsentWorkItem(isFni, _clientIp);
        if (_formModel.form.workItem == null) {
            _formModel.form.workItem = {
                digitalConsentBackFile: workItem
            };
        } else {
            _formModel.form.workItem.digitalConsentBackFile = workItem;
        }
        
    }

}

function performJointCheck() {
    _checkPerformed = true;
    let applicationUpdatedWithKyc = IdVerificationHelper.verifyKycUpdate(_formModel);
    let jointRequest = {
        userType: window.NOA.userContext.userType,
        investorList: applicationUpdatedWithKyc.form.yourDetails.investorList,
        verificationResult: _mobileNumberVerificationResult
    };
    _dcEligibility = DigitalConsentEligibilityHelper.jointEligibilityCheck(jointRequest);
}

function setReviewData(formModel, consolidatedFormModel, product) {
    _formModel = _.cloneDeep(formModel);
    _consolidatedFormModel = _.cloneDeep(consolidatedFormModel);
    _accountType = _.cloneDeep(formModel.form.accountType);
    _product = _.cloneDeep(product);
}

function setIdentificationMode(data) {
    _clientIdentificationMode = data;
    _formModel = IdVerificationHelper.getUpdateFormModelWithKycTypeToUpdate(_formModel, data.kycTypeToUpdate);
}

function setApplicationId(id, wipId) {
    _formModel.id = id;
    _consolidatedFormModel.id = id;
    if (wipId) {
        _formModel.wipId = wipId;
        _consolidatedFormModel.wipId = wipId;
    }
}

function setAccountNumber(accountNumber){
    _hasSubmitFailed = false;
    _accountNumber = accountNumber;
}

//TODO:: REFACTOR THIS INTO A DOWNLOAD MENU STORE
function toggleDownloadMenu(showDownloadMenu) {
    _showDownloadMenu = showDownloadMenu;
    if (showDownloadMenu && _product && _product.value) {
        _formsToDownload = DownloadFormsHelper.getFormsList(_product.value, _accountType);
    } else {
        _formsToDownload = [];
    }
    _selectedForms = [];
    _downloadInProgress = false;
    _downloadFormsFailed = false;
}

function updateFormsToDownload(index, checked) {
    if (_formsToDownload[index]) {
        _formsToDownload[index].checked = checked;
    }
}

function getSelectedForms() {
    if (_formsToDownload && _formsToDownload.length > 0) {
        _selectedForms = DownloadFormsHelper.getSelectedForms(_formsToDownload);
        _downloadInProgress = true;
    }
}

function receiveDownloadedApplicationForm(data) {
    if (data.attachments && data.attachments.length > 0) {
        let url = DownloadConstants.Base64String;
        _downloadedForms = url.replace('{contentType}', data.attachments[0].contentType).replace('{base64}', data.attachments[0].body);
    }
    _downloadFormsFailed = false;
    _formDownloaded = false;
    _downloadInProgress = false;
    _showDownloadMenu = false;
    _formsToDownload = [];
    _selectedForms = [];
}

//TODO:: REFACTOR THIS INTO A DOWNLOAD MENU STORE

function updateFormModelWorkItem(workItem) {
    _formModel.form.workItem = WorkItemHelper.getUpdatedFormModel(_formModel, workItem);
}

function setCommSeeBackFile(data) {
    const commSeeBackFile = WorkItemHelper.getCommSeeBackFile(data, _formModel);
    _formModel.form.workItem = WorkItemHelper.getUpdatedFormModel(_formModel, { idVerificationBackFile: commSeeBackFile });
}

function updateFormModelIdVerification(idVerificationModel) {
    _formModel.form.idVerification = _.cloneDeep(idVerificationModel);
}

function updateYourDetailsForSave() {
    if (_formModel.form) {
        _formModel.form.yourDetails =
            IdVerificationHelper.formatVerificationDocuments(_formModel.productId, _formModel.form.accountType, _formModel.form.yourDetails, _formModel.form.idVerification);
    }
}

function agreeIdVerificationForm() {
    _formModel.form.yourDetails =
        IdVerificationHelper.formatVerificationDocuments(_formModel.productId, _formModel.form.accountType, _formModel.form.yourDetails, _formModel.form.idVerification, true);
}

function receiveMobileNumberVerificationResult(result) {
    _mobileNumberVerificationResult = result;
    performDigitalConsentEligibilityCheck();
}

class DeclarationStoreClass extends EventEmitter {
    constructor() {
        super();
    }
    addChangeListener(cb) {
        this.on(CHANGE_EVENT, cb);
    }

    removeChangeListener(cb) {
        this.removeListener(CHANGE_EVENT, cb);
    }

    emitChange() {
        return setTimeout(()=>this.emit(CHANGE_EVENT), 0);
    }

    getFormModel() {
        return _formModel;
    }

    getConsolidatedFormModel() {
        return _consolidatedFormModel;
    }

    getProduct() {
        return _product;
    }

    getEligibility() {
        return _dcEligibility;
    }

    getShowHideMore() {
        return _showMoreMenu;
    }

    getAccountNumber(){
        return _accountNumber;
    }

    getHasSubmitFailed(){
        return _hasSubmitFailed;
    }

    getClientIdentificationMode(){
        return _clientIdentificationMode;
    }

    getAccountType() {
        return _accountType;
    }

    getShowDownloadMenu() {
        return _showDownloadMenu;
    }
    getFormsToDownload() {
        return _formsToDownload;
    }
    getEnableDownloadButton() {
        let checked = _.where(_formsToDownload, {'checked':true});
        return checked && checked.length > 0;
    }
    getDownloadInProgress() {
        return _downloadInProgress;
    }
    getSelectedForms() {
        return _selectedForms;
    }
    getReadyForDownload() {
        return _readyForDownload === true && _downloadInProgress === true;
    }
    getDownloadedForms() {
        return _downloadedForms;
    }
    getIsFormsDownloaded() {
        return _formDownloaded;
    }
    getDownloadFormsFailed() {
        if (_downloadInProgress) {
            return false;
        }
        return _downloadFormsFailed;
    }
    getApplicationSubmitting() {
        return _applicationSubmitting;
    }
    getDigitalConsentCheckEnabled() {
        return _digitalConsentCheckEnabled;
    }
    getWaitingForDigitalConsentKillSwitch() {
        return _waitingForDigitalConsentKillSwitch;
    }
    canAgreeDeclaration() {
        return _.get(window, 'NOA.entitlements.canAgreeDeclaration', true);
    }
    isSpinnerHidden() {
        return _hideSpinner;
    }
}

// Initialize the singleton to register with the
// dispatcher and export for React components
const DeclarationStore = new DeclarationStoreClass();

// Register each of the actions with the dispatcher
// by changing the store's data and emitting a change
DeclarationStore.dispatchToken = AppDispatcher.register((action) => {

    switch (action.actionType) {
        case ApplicationActionTypes.APPLICATION_SAVE_REQUEST:
        case ApplicationActionTypes.APPLICATION_SAVE_STARTING:
            updateYourDetailsForSave();
            break;
        case ApplicationActionTypes.AGREE_IDVERIFICATION_FORM:
            agreeIdVerificationForm();
            break;
        case ApplicationActionTypes.REVIEW_APPLICATION:
            setReviewData(action.formModel, action.consolidatedFormModel, action.product);
            break;
        case ApplicationActionTypes.UPDATE_WORKITEM:
            updateFormModelWorkItem(action.workItem);
            break;
        case ApplicationActionTypes.SET_COMMSEE_BACKFILE:
            setCommSeeBackFile(action.data);
            break;
        case ApplicationActionTypes.UPDATE_VERIFICATOIN_DOCUMENT:
            updateFormModelIdVerification(action.data);
            break;
        case ApplicationActionTypes.SHOW_MORE_MENU:
            _showMoreMenu = action.showMoreMenu;
            break;
        case ApplicationActionTypes.APPLICATION_SAVE_SUCCESS:
            setApplicationId(action.applicationId, action.wipId);
            break;
        case ApplicationActionTypes.APPLICATION_SUBMIT_SUCCESS:
            _applicationSubmitting = false;
            setAccountNumber(action.data.accountNumber);
            break;
        case ApplicationActionTypes.APPLICATION_SUBMIT_FAILED:
            _applicationSubmitting = false;
            resetStore(true);
            _formModel.submitDate = '';
            break;
        case ApplicationActionTypes.CLIENT_IDENTIFICATION_MODE_LOADED:
            setIdentificationMode(action.data);
            break;
        case ApplicationActionTypes.SHOW_DOWNLOAD_MENU:
            toggleDownloadMenu(action.showDownloadMenu);
            break;
        case ApplicationActionTypes.UPDATE_FORMS_TO_DOWNLOAD:
            updateFormsToDownload(action.index, action.checked);
            break;
        case ApplicationActionTypes.PREPARE_FORMS_TO_DOWNLOAD:
            getSelectedForms();
            break;
        case ApplicationActionTypes.APPLICATION_DOWNLOAD_REQUEST:
            _readyForDownload = true;
            break;
        case ApplicationActionTypes.START_DOWNLOADING_FORMS:
            _readyForDownload = false;
            break;
        case ApplicationActionTypes.DOWNLOAD_FORMS_SUCCESS:
            receiveDownloadedApplicationForm(action.data);
            break;
        case ApplicationActionTypes.CLEAR_DOWNLOADED_FORMS:
            _downloadedForms = undefined;
            _formDownloaded = true;
            _selectedForms = [];
            break;
        case ApplicationActionTypes.DOWNLOAD_FORMS_FAILED:
            _downloadInProgress = false;
            _formDownloaded = true;
            _downloadFormsFailed = true;
            break;
        case ApplicationActionTypes.APPLICATION_SUBMIT_STARTING:
            _applicationSubmitting = true;
            _hasSubmitFailed = undefined;
            _accountNumber = '';
            break;
        case ApplicationActionTypes.DECLARATION_DISAGREED:
            resetStore(false);
            break;
        case DCActionTypes.RECEIVED_VERIFY_MOBILE_NUMBER_RESULT:
            receiveMobileNumberVerificationResult(action.result);
            break;
        case ApplicationActionTypes.DIGITALCONSENT_KILLSWITCH_STARTING:
            _waitingForDigitalConsentKillSwitch = true;
            break;
        case ApplicationActionTypes.DIGITALCONSENT_KILLSWITCH_ERROR:
            _waitingForDigitalConsentKillSwitch = false;
            break;
        case ApplicationActionTypes.DIGITALCONSENT_KILLSWITCH_SUCCESS:
            _waitingForDigitalConsentKillSwitch = false;
            setDigitalConsentKillSwitch(action.killSwitch);
            break;
        case ActionTypes.GO_TO_ELECTRONIC_SUBMISSION_PAGE:
            _digitalConsentCheckEnabled = false;
            break;
        case DeclarationConstants.HIDE_SPINNER:
            _hideSpinner = action.hideSpinner;
            break;
        case ActionTypes.RECEIVE_CLIENT_IP_ADDRESS:
            _clientIp = action.result;
            _digitalConsentCheckEnabled = false;
            performDigitalConsentEligibilityCheck(action.result);
            break;
        case ActionTypes.TOGGLE_ELIGIBILITY_CHECK_COMPLETE:
            toggleEligibilityCheckComplete();
            break;
        default:
            return true;
    }

    DeclarationStore.emitChange();
    return true;
});

export default DeclarationStore;


// WEBPACK FOOTER //
// C:/GH/1106/advice.noa.origination/~/eslint-loader!./stores/DeclarationStore.js